import { useEffect, useState, useRef } from "react";
import "./App.css";

function App() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [navScrolled, setNavScrolled] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [people, setPeople] = useState(2);
  const [toastMessage, setToastMessage] = useState("");
  const [toastType, setToastType] = useState("success");
  const [showToast, setShowToast] = useState(false);
  const [formLoading, setFormLoading] = useState(false);
  const [flippedCards, setFlippedCards] = useState({});
  const [countersAnimated, setCountersAnimated] = useState(false);
  
  // Calculator state
  const [area, setArea] = useState(25);
  const [height, setHeight] = useState("2.6");
  const [orientation, setOrientation] = useState("nord");
  const [room, setRoom] = useState("dormitor");
  const [insulation, setInsulation] = useState("15cm");
  
  // Form state
  const [formData, setFormData] = useState({
    service: "",
    name: "",
    phone: "",
    email: "",
    address: "",
    urgency: "În această săptămână",
    message: ""
  });

  // Review form state
  const [reviewData, setReviewData] = useState({
    name: "",
    rating: 0,
    review: ""
  });
  const [reviewSubmitted, setReviewSubmitted] = useState(false);

  const statsRef = useRef(null);
  const countersRef = useRef([]);

  // Scroll handler for navbar and reveal animations
  useEffect(() => {
    const handleScroll = () => {
      setNavScrolled(window.scrollY > 50);
      
      // Animate counters on scroll
      if (!countersAnimated && statsRef.current) {
        const rect = statsRef.current.getBoundingClientRect();
        if (rect.top < window.innerHeight - 100) {
          setCountersAnimated(true);
          animateCounters();
        }
      }
      
      // Reveal animations
      const revealElements = document.querySelectorAll('.reveal');
      revealElements.forEach(el => {
        const windowHeight = window.innerHeight;
        const elementTop = el.getBoundingClientRect().top;
        const revealPoint = 150;
        
        if (elementTop < windowHeight - revealPoint) {
          el.classList.add('active');
        }
      });
    };
    
    window.addEventListener("scroll", handleScroll);
    handleScroll(); // Initial check
    return () => window.removeEventListener("scroll", handleScroll);
  }, [countersAnimated]);

  // Create particles on mount
  useEffect(() => {
    const container = document.getElementById("particles");
    if (container) {
      container.innerHTML = "";
      for (let i = 0; i < 30; i++) {
        const particle = document.createElement("div");
        particle.className = "particle";
        particle.style.left = Math.random() * 100 + "%";
        particle.style.animationDelay = Math.random() * 15 + "s";
        particle.style.animationDuration = (10 + Math.random() * 10) + "s";
        container.appendChild(particle);
      }
    }
  }, []);

  // Custom cursor
  useEffect(() => {
    const cursor = document.getElementById("cursor");
    if (window.matchMedia("(hover: hover) and (pointer: fine)").matches && cursor) {
      const moveCursor = (e) => {
        cursor.style.left = e.clientX - 10 + "px";
        cursor.style.top = e.clientY - 10 + "px";
      };
      document.addEventListener("mousemove", moveCursor);
      return () => document.removeEventListener("mousemove", moveCursor);
    }
  }, []);

  const animateCounters = () => {
    const targets = [8, 100];
    const suffixes = ["+", ""];
    
    countersRef.current.forEach((counter, index) => {
      if (!counter) return;
      const target = targets[index];
      const suffix = suffixes[index];
      const duration = 2000;
      const increment = target / (duration / 16);
      let current = 0;
      
      const updateCounter = () => {
        current += increment;
        if (current < target) {
          counter.textContent = Math.ceil(current) + suffix;
          requestAnimationFrame(updateCounter);
        } else {
          counter.textContent = target + suffix;
        }
      };
      updateCounter();
    });
  };

  // BTU Calculator - Standard formula: ~25 BTU per cubic foot or ~600 BTU per m²
  const calculateBTU = (area, height, orientation, room, insulation, people) => {
    // Base calculation: area * 600 BTU per m² (standard for moderate climate)
    let btu = Number(area) * 600;
    
    // Height adjustment (standard is 2.5m)
    const heightMultiplier = parseFloat(height) / 2.5;
    btu *= heightMultiplier;
    
    // Orientation adjustment
    if (orientation === "sud") btu *= 1.15;
    else if (orientation === "vest") btu *= 1.10;
    else if (orientation === "est") btu *= 1.05;
    else if (orientation === "nord") btu *= 0.95;
    
    // Room type adjustment
    if (room === "bucatarie") btu *= 1.30;
    else if (room === "birou") btu *= 1.20;
    else if (room === "living") btu *= 1.15;
    
    // Insulation adjustment
    if (insulation === "fara") btu *= 1.40;
    else if (insulation === "10cm") btu *= 1.15;
    else if (insulation === "15cm") btu *= 1.00;
    else if (insulation === "peste15") btu *= 0.85;
    
    // People adjustment (each person adds ~600 BTU)
    btu += Number(people) * 600;
    
    // Round to nearest standard BTU value
    const standards = [9000, 12000, 18000, 24000, 36000];
    return standards.find(s => s >= btu) || 36000;
  };

  const btuResult = calculateBTU(area, height, orientation, room, insulation, people);
  const formattedBtu = btuResult.toLocaleString("ro-RO");
  
  let energyClass = "Clasa A+++ ❄❄❄";
  if (btuResult > 18000) energyClass = "Clasa A++ ❄❄";
  if (btuResult > 24000) energyClass = "Clasa A+ ❄";

  const standards = [9000, 12000, 18000, 24000, 36000];
  let minBtu = standards[standards.indexOf(btuResult) - 1] || btuResult - 3000;

  const displayToast = (message, type = "success") => {
    setToastMessage(message);
    setToastType(type);
    setShowToast(true);
    setTimeout(() => setShowToast(false), 4000);
  };

  const toggleCard = (cardId) => {
    setFlippedCards(prev => ({
      ...prev,
      [cardId]: !prev[cardId]
    }));
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const nextStep = (step) => {
    if (currentStep === 1 && !formData.service) {
      displayToast("Te rugăm să selectezi un serviciu", "error");
      return;
    }
    
    if (currentStep === 2) {
      if (!formData.name || !formData.phone || !formData.address) {
        displayToast("Te rugăm să completezi câmpurile obligatorii", "error");
        return;
      }
      if (!/^[0-9+\s]{10,15}$/.test(formData.phone.replace(/\s/g, ""))) {
        displayToast("Te rugăm să introduci un număr de telefon valid", "error");
        return;
      }
    }
    
    setCurrentStep(step);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    
    // EMAILJS SETUP: Replace YOUR_EMAILJS_PUBLIC_KEY, YOUR_SERVICE_ID, YOUR_TEMPLATE_ID 
    // with values from emailjs.com dashboard
    const templateParams = {
      from_name: formData.name,
      phone: formData.phone,
      email: formData.email || "Nu a fost furnizat",
      address: formData.address,
      service: formData.service,
      urgency: formData.urgency,
      details: formData.message || "Fără detalii suplimentare",
      to_email: "aerinstaliasi@gmail.com"
    };

    // Check if EmailJS is loaded
    if (typeof window.emailjs !== 'undefined') {
      try {
        await window.emailjs.send("YOUR_SERVICE_ID", "YOUR_TEMPLATE_ID", templateParams);
        setFormLoading(false);
        displayToast("✅ Cererea ta a fost trimisă! Te contactăm în cel mai scurt timp.", "success");
        setFormData({
          service: "",
          name: "",
          phone: "",
          email: "",
          address: "",
          urgency: "În această săptămână",
          message: ""
        });
        setCurrentStep(1);
      } catch (error) {
        setFormLoading(false);
        displayToast("❌ Eroare la trimitere. Sună-ne direct la numărul de telefon.", "error");
        console.error("EmailJS Error:", error);
      }
    } else {
      // Fallback if EmailJS not loaded - simulate success
      setTimeout(() => {
        setFormLoading(false);
        displayToast("✅ Cererea ta a fost trimisă! Te contactăm în cel mai scurt timp.", "success");
        console.log("Form data (EmailJS not configured):", templateParams);
        setFormData({
          service: "",
          name: "",
          phone: "",
          email: "",
          address: "",
          urgency: "În această săptămână",
          message: ""
        });
        setCurrentStep(1);
      }, 1500);
    }
  };

  // Review form handlers
  const handleReviewChange = (e) => {
    const { name, value } = e.target;
    setReviewData(prev => ({ ...prev, [name]: value }));
  };

  const setRating = (rating) => {
    setReviewData(prev => ({ ...prev, rating }));
  };

  const handleReviewSubmit = (e) => {
    e.preventDefault();
    
    if (!reviewData.name || !reviewData.rating || !reviewData.review) {
      displayToast("Te rugăm să completezi toate câmpurile", "error");
      return;
    }

    // Store in localStorage
    const reviews = JSON.parse(localStorage.getItem("aerinstal_reviews") || "[]");
    reviews.push({
      ...reviewData,
      date: new Date().toISOString()
    });
    localStorage.setItem("aerinstal_reviews", JSON.stringify(reviews));

    // Show success
    setReviewSubmitted(true);
    
    // Reset form after 3 seconds
    setTimeout(() => {
      setReviewData({ name: "", rating: 0, review: "" });
      setReviewSubmitted(false);
    }, 5000);
  };

  const scrollToSection = (e, sectionId) => {
    e.preventDefault();
    const element = document.getElementById(sectionId);
    if (element) {
      const navHeight = 80;
      const targetPosition = element.getBoundingClientRect().top + window.pageYOffset - navHeight;
      window.scrollTo({ top: targetPosition, behavior: "smooth" });
    }
    setMobileMenuOpen(false);
  };

  return (
    <div className="aer-app">
      {/* Custom Cursor */}
      <div className="cursor" id="cursor"></div>

      {/* Navbar */}
      <nav className={`navbar ${navScrolled ? "scrolled" : ""}`} data-testid="navbar">
        <div className="container">
          <a href="#acasa" className="logo" onClick={(e) => scrollToSection(e, "acasa")}>
            <img 
              src="https://customer-assets.emergentagent.com/job_aer-instal-iasi/artifacts/zsz1a59d_logoaerinstaliasi.png" 
              alt="AER INSTAL IAȘI Logo" 
              className="logo-img"
            />
            <span>AER INSTAL IAȘI</span>
          </a>
          
          <ul className="nav-links">
            <li><a href="#acasa" onClick={(e) => scrollToSection(e, "acasa")}>Acasă</a></li>
            <li><a href="#servicii" onClick={(e) => scrollToSection(e, "servicii")}>Servicii</a></li>
            <li><a href="#proiecte" onClick={(e) => scrollToSection(e, "proiecte")}>Proiecte</a></li>
            <li><a href="#despre" onClick={(e) => scrollToSection(e, "despre")}>Despre Noi</a></li>
            <li><a href="#calculator" onClick={(e) => scrollToSection(e, "calculator")}>Calculator BTU</a></li>
            <li><a href="#contact" onClick={(e) => scrollToSection(e, "contact")}>Contact</a></li>
            <li><a href="#contact" className="btn btn-primary nav-cta" onClick={(e) => scrollToSection(e, "contact")} data-testid="nav-cta-btn">Solicită Ofertă</a></li>
          </ul>
          
          <div 
            className={`hamburger ${mobileMenuOpen ? "active" : ""}`} 
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="hamburger-menu"
          >
            <span></span>
            <span></span>
            <span></span>
          </div>
        </div>
      </nav>

      {/* Mobile Menu */}
      <div className={`mobile-menu ${mobileMenuOpen ? "active" : ""}`}>
        <a href="#acasa" onClick={(e) => scrollToSection(e, "acasa")}>Acasă</a>
        <a href="#servicii" onClick={(e) => scrollToSection(e, "servicii")}>Servicii</a>
        <a href="#proiecte" onClick={(e) => scrollToSection(e, "proiecte")}>Proiecte</a>
        <a href="#despre" onClick={(e) => scrollToSection(e, "despre")}>Despre Noi</a>
        <a href="#calculator" onClick={(e) => scrollToSection(e, "calculator")}>Calculator BTU</a>
        <a href="#contact" onClick={(e) => scrollToSection(e, "contact")}>Contact</a>
        <a href="#contact" className="btn btn-primary" onClick={(e) => scrollToSection(e, "contact")}>Solicită Ofertă</a>
      </div>

      {/* Hero Section */}
      <section className="hero" id="acasa">
        <div className="hero-particles" id="particles"></div>
        
        {/* AC Illustration */}
        <svg className="hero-illustration" viewBox="0 0 300 200" fill="none" xmlns="http://www.w3.org/2000/svg">
          <rect x="30" y="40" width="240" height="100" rx="10" fill="#FFFFFF" stroke="#06B6D4" strokeWidth="3"/>
          <rect x="50" y="55" width="200" height="15" rx="3" fill="#E5E7EB"/>
          <rect x="50" y="80" width="200" height="40" rx="5" fill="#F0F4FF" stroke="#2563EB" strokeWidth="1"/>
          <circle cx="70" cy="100" r="8" fill="#22C55E"/>
          <rect x="180" y="90" width="50" height="20" rx="5" fill="#2563EB"/>
          <text x="195" y="104" fill="#FFFFFF" fontSize="10" fontFamily="Inter">24°C</text>
          <path d="M100 140 L100 180 M200 140 L200 180" stroke="#06B6D4" strokeWidth="4"/>
        </svg>
        
        {/* Cold Air Waves */}
        <div className="cold-waves">
          <div className="wave"></div>
          <div className="wave"></div>
          <div className="wave"></div>
          <div className="wave"></div>
          <div className="wave"></div>
        </div>
        
        <div className="hero-content">
          <div className="hero-badge">
            <i className="fas fa-snowflake"></i> Autorizat AGFR
          </div>
          <h1>AER INSTAL IAȘI</h1>
          <p>Montaj, igienizare și service aer condiționat în Iași. Rapid, garantat, profesional.</p>
          <div className="hero-btns">
            <a href="#contact" className="btn btn-primary" onClick={(e) => scrollToSection(e, "contact")} data-testid="hero-cta-btn">
              <i className="fas fa-paper-plane"></i> Solicită Ofertă Gratuită
            </a>
            <a href="#calculator" className="btn btn-secondary" onClick={(e) => scrollToSection(e, "calculator")} data-testid="hero-calculator-btn">
              <i className="fas fa-calculator"></i> Calculează BTU
            </a>
          </div>
        </div>
        
        <div className="scroll-indicator">
          <span>Scroll</span>
          <i className="fas fa-chevron-down"></i>
        </div>
      </section>

      {/* Trust Bar */}
      <div className="trust-bar">
        <div className="marquee">
          <div className="marquee-content">
            <div className="marquee-item"><i className="fas fa-check-circle"></i> Montaj Profesional</div>
            <div className="marquee-item"><i className="fas fa-snowflake"></i> Aer Curat Permanent</div>
            <div className="marquee-item"><i className="fas fa-certificate"></i> Echipă Autorizată</div>
            <div className="marquee-item"><i className="fas fa-bolt"></i> Intervenție Rapidă</div>
            <div className="marquee-item"><i className="fas fa-shield-alt"></i> Garanție Completă</div>
            <div className="marquee-item"><i className="fas fa-tools"></i> Toate Brandurile</div>
          </div>
          <div className="marquee-content">
            <div className="marquee-item"><i className="fas fa-check-circle"></i> Montaj Profesional</div>
            <div className="marquee-item"><i className="fas fa-snowflake"></i> Aer Curat Permanent</div>
            <div className="marquee-item"><i className="fas fa-certificate"></i> Echipă Autorizată</div>
            <div className="marquee-item"><i className="fas fa-bolt"></i> Intervenție Rapidă</div>
            <div className="marquee-item"><i className="fas fa-shield-alt"></i> Garanție Completă</div>
            <div className="marquee-item"><i className="fas fa-tools"></i> Toate Brandurile</div>
          </div>
        </div>
      </div>

      {/* Services Section */}
      <section className="services" id="servicii">
        <div className="container">
          <h2 className="section-title reveal">Serviciile Noastre</h2>
          <p className="section-subtitle reveal">Apasă pe un serviciu pentru a vedea detaliile</p>
          
          <div className="flip-cards-grid">
            {/* Card 1 - Montaj */}
            <div 
              className={`flip-card reveal ${flippedCards["montaj"] ? "flipped" : ""}`} 
              onClick={() => toggleCard("montaj")}
              data-testid="flip-card-montaj"
            >
              <div className="flip-card-inner">
                <div className="flip-card-front with-bg" style={{backgroundImage: "url('https://customer-assets.emergentagent.com/job_aer-instal-iasi/artifacts/torff7ka_imaginesite1.png')"}}>
                  <div className="card-overlay"></div>
                  <div className="icon"><i className="fas fa-snowflake"></i></div>
                  <h3>Montaj Aer Condiționat</h3>
                  <span>Instalare profesională</span>
                </div>
                <div className="flip-card-back">
                  <h4>Detalii Serviciu</h4>
                  <ul>
                    <li><i className="fas fa-check"></i> Instalare orice brand/model</li>
                    <li><i className="fas fa-check"></i> Verificare completă post-montaj</li>
                    <li><i className="fas fa-check"></i> Garanție lucrare inclusă</li>
                    <li><i className="fas fa-check"></i> Beton, BCA, cărămidă</li>
                  </ul>
                  <a href="#contact" className="btn btn-primary" onClick={(e) => scrollToSection(e, "contact")}>Află Mai Mult <i className="fas fa-arrow-right"></i></a>
                </div>
              </div>
            </div>
            
            {/* Card 2 - Igienizare */}
            <div 
              className={`flip-card reveal ${flippedCards["igienizare"] ? "flipped" : ""}`} 
              onClick={() => toggleCard("igienizare")}
              data-testid="flip-card-igienizare"
            >
              <div className="flip-card-inner">
                <div className="flip-card-front with-bg" style={{backgroundImage: "url('https://customer-assets.emergentagent.com/job_aer-instal-iasi/artifacts/3urhbwzs_imaginesite2.png')"}}>
                  <div className="card-overlay"></div>
                  <div className="icon"><i className="fas fa-spray-can"></i></div>
                  <h3>Igienizare</h3>
                  <span>Curățare profundă cu abur</span>
                </div>
                <div className="flip-card-back">
                  <h4>Detalii Serviciu</h4>
                  <ul>
                    <li><i className="fas fa-check"></i> Curățare filtre, evaporator, condensator</li>
                    <li><i className="fas fa-check"></i> Dezinfecție antibacteriană</li>
                    <li><i className="fas fa-check"></i> Elimină mirosuri și bacterii</li>
                    <li><i className="fas fa-check"></i> Recomandat anual</li>
                  </ul>
                  <a href="#contact" className="btn btn-primary" onClick={(e) => scrollToSection(e, "contact")}>Află Mai Mult <i className="fas fa-arrow-right"></i></a>
                </div>
              </div>
            </div>
            
            {/* Card 3 - Freon */}
            <div 
              className={`flip-card reveal ${flippedCards["freon"] ? "flipped" : ""}`} 
              onClick={() => toggleCard("freon")}
              data-testid="flip-card-freon"
            >
              <div className="flip-card-inner">
                <div className="flip-card-front with-bg" style={{backgroundImage: "url('https://customer-assets.emergentagent.com/job_aer-instal-iasi/artifacts/w2vgzvvp_imaginesite3.png')"}}>
                  <div className="card-overlay"></div>
                  <div className="icon"><i className="fas fa-thermometer-half"></i></div>
                  <h3>Încărcare Freon</h3>
                  <span>Reîncărcare și verificare gaz</span>
                </div>
                <div className="flip-card-back">
                  <h4>Detalii Serviciu</h4>
                  <ul>
                    <li><i className="fas fa-check"></i> Detectare pierderi freon</li>
                    <li><i className="fas fa-check"></i> Reîncărcare R32/R410A</li>
                    <li><i className="fas fa-check"></i> Verificare presiune sistem</li>
                    <li><i className="fas fa-check"></i> Raport tehnic inclus</li>
                  </ul>
                  <a href="#contact" className="btn btn-primary" onClick={(e) => scrollToSection(e, "contact")}>Află Mai Mult <i className="fas fa-arrow-right"></i></a>
                </div>
              </div>
            </div>
            
            {/* Card 4 - Carotare */}
            <div 
              className={`flip-card reveal ${flippedCards["carotare"] ? "flipped" : ""}`} 
              onClick={() => toggleCard("carotare")}
              data-testid="flip-card-carotare"
            >
              <div className="flip-card-inner">
                <div className="flip-card-front with-bg" style={{backgroundImage: "url('https://customer-assets.emergentagent.com/job_aer-instal-iasi/artifacts/l67etd31_imaginesite4.png')"}}>
                  <div className="card-overlay"></div>
                  <div className="icon"><i className="fas fa-cog"></i></div>
                  <h3>Carotare</h3>
                  <span>Găuri precise în orice material</span>
                </div>
                <div className="flip-card-back">
                  <h4>Detalii Serviciu</h4>
                  <ul>
                    <li><i className="fas fa-check"></i> Beton armat, cărămidă, BCA</li>
                    <li><i className="fas fa-check"></i> Diametre 40–200mm</li>
                    <li><i className="fas fa-check"></i> Tehnologie cu apă (fără praf)</li>
                    <li><i className="fas fa-check"></i> Finisaj curat garantat</li>
                  </ul>
                  <a href="#contact" className="btn btn-primary" onClick={(e) => scrollToSection(e, "contact")}>Află Mai Mult <i className="fas fa-arrow-right"></i></a>
                </div>
              </div>
            </div>
          </div>
          
          <p className="tap-hint"><i className="fas fa-hand-pointer"></i> Click pentru detalii</p>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="why-us" id="despre">
        <div className="container">
          <h2 className="section-title reveal">De Ce Să Ne Alegi?</h2>
          <p className="section-subtitle reveal">Experiență, profesionalism și dedicare pentru confortul tău</p>
          
          <div className="stats-grid" ref={statsRef}>
            <div className="stat-card reveal">
              <div className="stat-number" ref={el => countersRef.current[0] = el}>0</div>
              <div className="stat-label">Ani Experiență</div>
            </div>
            <div className="stat-card reveal">
              <div className="stat-number" ref={el => countersRef.current[1] = el}>0</div>
              <div className="stat-label">% Garanție Lucrări</div>
            </div>
          </div>
          
          <div className="features-grid">
            <div className="feature-card reveal">
              <i className="fas fa-shield-alt"></i>
              <div>
                <h4>Echipă Autorizată AGFR</h4>
                <p>Tehnicieni certificați cu experiență vastă în domeniu</p>
              </div>
            </div>
            <div className="feature-card reveal">
              <i className="fas fa-bolt"></i>
              <div>
                <h4>Intervenție în max. 24h</h4>
                <p>Răspundem rapid la solicitări în Iași și împrejurimi</p>
              </div>
            </div>
            <div className="feature-card reveal">
              <i className="fas fa-tools"></i>
              <div>
                <h4>Toate Brandurile</h4>
                <p>Daikin, Mitsubishi, Samsung, LG, Gree și multe altele</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Process Timeline */}
      <section className="process">
        <div className="container">
          <h2 className="section-title reveal">Cum Funcționează?</h2>
          <p className="section-subtitle reveal">4 pași simpli pentru confortul tău termic</p>
          
          <div className="timeline">
            <div className="timeline-step reveal">
              <div className="step-number">1</div>
              <div className="step-content">
                <div className="step-icon">📞</div>
                <h4>Contactează-ne</h4>
                <p>Sună sau completează formularul de contact</p>
              </div>
            </div>
            <div className="timeline-step reveal">
              <div className="step-number">2</div>
              <div className="step-content">
                <div className="step-icon">📋</div>
                <h4>Evaluare gratuită</h4>
                <p>Îți trimitem un specialist pentru evaluare</p>
              </div>
            </div>
            <div className="timeline-step reveal">
              <div className="step-number">3</div>
              <div className="step-content">
                <div className="step-icon">🔧</div>
                <h4>Execuție rapidă</h4>
                <p>Montaj sau service în ziua stabilită</p>
              </div>
            </div>
            <div className="timeline-step reveal">
              <div className="step-number">4</div>
              <div className="step-content">
                <div className="step-icon">✅</div>
                <h4>Garanție & suport</h4>
                <p>Garanție completă și asistență post-serviciu</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* BTU Calculator */}
      <section className="calculator" id="calculator">
        <div className="container">
          <h2 className="section-title reveal">Calculator BTU</h2>
          <p className="section-subtitle reveal">Află Puterea Necesară pentru Camera Ta</p>
          
          <div className="calculator-wrapper">
            <div className="calc-form reveal">
              <div className="form-group">
                <label>Suprafața camerei (m²)</label>
                <input 
                  type="range" 
                  min="10" 
                  max="100" 
                  value={area}
                  onChange={(e) => setArea(parseInt(e.target.value))}
                  data-testid="btu-area-slider"
                />
                <span className="range-value">{area} m²</span>
              </div>
              
              <div className="form-group">
                <label>Înălțimea tavanului</label>
                <select value={height} onChange={(e) => setHeight(e.target.value)} data-testid="btu-height-select">
                  <option value="2.4">2.4 m</option>
                  <option value="2.6">2.6 m</option>
                  <option value="2.8">2.8 m</option>
                  <option value="3.0">3.0 m+</option>
                </select>
              </div>
              
              <div className="form-group">
                <label>Orientare ferestre</label>
                <div className="radio-group">
                  {["nord", "sud", "est", "vest"].map(dir => (
                    <div className="radio-option" key={dir}>
                      <input 
                        type="radio" 
                        name="orientation" 
                        id={dir} 
                        value={dir}
                        checked={orientation === dir}
                        onChange={(e) => setOrientation(e.target.value)}
                      />
                      <label htmlFor={dir}>{dir.charAt(0).toUpperCase() + dir.slice(1)}</label>
                    </div>
                  ))}
                </div>
              </div>
              
              <div className="form-group">
                <label>Tip cameră</label>
                <div className="radio-group">
                  {[
                    { id: "dormitor", label: "Dormitor" },
                    { id: "living", label: "Living" },
                    { id: "birou", label: "Birou" },
                    { id: "bucatarie", label: "Bucătărie" }
                  ].map(r => (
                    <div className="radio-option" key={r.id}>
                      <input 
                        type="radio" 
                        name="room" 
                        id={r.id} 
                        value={r.id}
                        checked={room === r.id}
                        onChange={(e) => setRoom(e.target.value)}
                      />
                      <label htmlFor={r.id}>{r.label}</label>
                    </div>
                  ))}
                </div>
              </div>
              
              <div className="form-group">
                <label>Izolație</label>
                <div className="radio-group">
                  {[
                    { id: "fara", label: "Fără Izolație" },
                    { id: "10cm", label: "10 cm" },
                    { id: "15cm", label: "15 cm" },
                    { id: "peste15", label: "Peste 15 cm" }
                  ].map(ins => (
                    <div className="radio-option" key={ins.id}>
                      <input 
                        type="radio" 
                        name="insulation" 
                        id={ins.id} 
                        value={ins.id}
                        checked={insulation === ins.id}
                        onChange={(e) => setInsulation(e.target.value)}
                      />
                      <label htmlFor={ins.id}>{ins.label}</label>
                    </div>
                  ))}
                </div>
              </div>
              
              <div className="form-group">
                <label>Nr. persoane în cameră</label>
                <div className="stepper">
                  <button type="button" onClick={() => setPeople(Math.max(1, people - 1))} data-testid="btu-people-minus">
                    <i className="fas fa-minus"></i>
                  </button>
                  <span data-testid="btu-people-count">{people}</span>
                  <button type="button" onClick={() => setPeople(Math.min(6, people + 1))} data-testid="btu-people-plus">
                    <i className="fas fa-plus"></i>
                  </button>
                </div>
              </div>
            </div>
            
            <div className="calc-result reveal">
              <div className="result-display">
                <div className="result-label">Putere recomandată</div>
                <div className={`result-value ${btuResult >= 100000 ? 'small' : btuResult >= 10000 ? 'medium' : ''}`} data-testid="btu-result">{formattedBtu}</div>
                <div className="result-unit">BTU</div>
                <div className="result-badge" data-testid="btu-energy-class">{energyClass}</div>
              </div>
              
              <div className="recommendation">
                <p data-testid="btu-recommendation">Pentru camera ta, îți recomandăm un aparat între {minBtu.toLocaleString("ro-RO")} – {formattedBtu} BTU</p>
                <a href="#contact" className="btn btn-primary" onClick={(e) => scrollToSection(e, "contact")}>
                  Solicită Ofertă pentru Aparatul Recomandat <i className="fas fa-arrow-right"></i>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Proiecte */}
      <section id="proiecte" className="projects-section">
        <div className="container">
          <h2 className="section-title reveal">Proiectele Noastre</h2>
          <p className="section-subtitle reveal">Lucrări realizate de echipa AER INSTAL IAȘI</p>
          
          <div className="projects-grid reveal">
            <div className="project-card">
              <div className="project-image-placeholder">
                <img src="https://i.ibb.co/gbKBjLxn/image0.jpg" alt="Proiect 1" />
              </div>
              <div className="project-info">
                <h3>Montaj Apartament</h3>
                <p>Iași, 2025</p>
              </div>
            </div>
            <div className="project-card">
              <div className="project-image-placeholder">
                <img src="https://i.ibb.co/zhd23NzH/image1.jpg" alt="Proiect 2" />
              </div>
              <div className="project-info">
                <h3>Montaj Vilă</h3>
                <p>Iași, 2025</p>
              </div>
            </div>
            <div className="project-card">
              <div className="project-image-placeholder">
                <img src="https://i.ibb.co/MxL7M3mL/image2.jpg" alt="Proiect 3" />
              </div>
              <div className="project-info">
                <h3>Igienizare Spațiu Comercial</h3>
                <p>Iași, 2025</p>
              </div>
            </div>
            <div className="project-card">
              <div className="project-image-placeholder">
                <img src="https://i.ibb.co/dJc85HzK/image3.jpg" alt="Proiect 4" />
              </div>
              <div className="project-info">
                <h3>Montaj Birou</h3>
                <p>Iași, 2025</p>
              </div>
            </div>
            <div className="project-card">
              <div className="project-image-placeholder">
                <img src="https://i.ibb.co/0V1RW1ML/image4.jpg" alt="Proiect 5" />
              </div>
              <div className="project-info">
                <h3>Carotare & Montaj</h3>
                <p>Iași, 2025</p>
              </div>
            </div>
            <div className="project-card">
              <div className="project-image-placeholder">
                <img src="https://i.ibb.co/LdwkVLrr/image5.jpg" alt="Proiect 6" />
              </div>
              <div className="project-info">
                <h3>Service Freon</h3>
                <p>Iași, 2025</p>
              </div>
            </div>
            <div className="project-card">
              <div className="project-image-placeholder">
                <img src="https://i.ibb.co/R4DJxjcT/image6.jpg" alt="Proiect 7" />
              </div>
              <div className="project-info">
                <h3>Montaj Rezidențial</h3>
                <p>Iași, 2026</p>
              </div>
            </div>
            <div className="project-card">
              <div className="project-image-placeholder">
                <img src="https://i.ibb.co/hJLwRcYJ/image7.jpg" alt="Proiect 8" />
              </div>
              <div className="project-info">
                <h3>Igienizare & Service</h3>
                <p>Iași, 2026</p>
              </div>
            </div>
            <div className="project-card">
              <div className="project-image-placeholder">
                <img src="https://i.ibb.co/zTDhGGwp/image8.jpg" alt="Proiect 9" />
              </div>
              <div className="project-info">
                <h3>Montaj Industrial</h3>
                <p>Iași, 2026</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="testimonials">
        <div className="container">
          <h2 className="section-title reveal">Ce Spun Clienții Noștri</h2>
          <p className="section-subtitle reveal">Feedback-ul clienților mulțumiți</p>
        </div>
        
        <div className="testimonial-carousel">
          {[...Array(2)].flatMap((_, repeat) => [
            {
              text: "Echipă punctuală și profesionistă. Au montat aparatul în 3 ore, totul curat și îngrijit. Recomand cu căldură!",
              name: "Andrei M.",
              location: "Iași",
              initials: "AM"
            },
            {
              text: "Am sunat dimineața, au venit după-amiaza. Prețuri corecte și lucrare de calitate. O să îi sun și pentru igienizare.",
              name: "Maria T.",
              location: "Iași",
              initials: "MT"
            },
            {
              text: "Aparatul meu nu mai răcea bine. L-au încărcat cu freon și verificat complet. Acum merge perfect. Mulțumesc!",
              name: "Constantin R.",
              location: "Iași",
              initials: "CR"
            }
          ].map((testimonial, index) => (
            <div className="testimonial-card" key={`${repeat}-${index}`}>
              <div className="testimonial-stars">
                {[...Array(5)].map((_, i) => <i key={i} className="fas fa-star"></i>)}
              </div>
              <p className="testimonial-text">"{testimonial.text}"</p>
              <div className="testimonial-author">
                <div className="author-avatar">{testimonial.initials}</div>
                <div className="author-info">
                  <h5>{testimonial.name}</h5>
                  <span>{testimonial.location}</span>
                </div>
              </div>
            </div>
          )))}
        </div>

        {/* Review Form Section */}
        <div className="container review-form-container">
          <div className="review-form-card">
            {!reviewSubmitted ? (
              <>
                <div className="review-form-header">
                  <h3><i className="fas fa-star"></i> Împărtășește Experiența Ta</h3>
                  <p>Recenzia ta ajută alți clienți să aleagă servicii de calitate</p>
                </div>
                <form onSubmit={handleReviewSubmit} className="review-form" data-testid="review-form">
                  <div className="form-group">
                    <input
                      type="text"
                      name="name"
                      className="form-input review-input"
                      placeholder="Nume complet *"
                      value={reviewData.name}
                      onChange={handleReviewChange}
                      data-testid="review-name"
                    />
                  </div>
                  <div className="form-group">
                    <label className="rating-label">Rating *</label>
                    <div className="star-rating" data-testid="review-stars">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <i
                          key={star}
                          className={`fas fa-star star-input ${star <= reviewData.rating ? 'active' : ''}`}
                          onClick={() => setRating(star)}
                          data-testid={`review-star-${star}`}
                        ></i>
                      ))}
                    </div>
                  </div>
                  <div className="form-group">
                    <textarea
                      name="review"
                      className="form-input review-input review-textarea"
                      placeholder="Descrie experiența ta cu serviciile noastre..."
                      rows="3"
                      value={reviewData.review}
                      onChange={handleReviewChange}
                      data-testid="review-text"
                    ></textarea>
                  </div>
                  <button type="submit" className="btn btn-submit review-submit" data-testid="review-submit">
                    <span className="btn-text">Trimite Recenzia <i className="fas fa-paper-plane"></i></span>
                  </button>
                </form>
              </>
            ) : (
              <div className="review-success" data-testid="review-success">
                <i className="fas fa-check-circle"></i>
                <p>Mulțumim pentru recenzie! O vom publica după verificare.</p>
              </div>
            )}
          </div>
        </div>
      </section>
      <section className="contact" id="contact">
        <div className="container">
          <h2 className="section-title reveal">Contactează-ne</h2>
          <p className="section-subtitle reveal">Solicită o ofertă gratuită sau programează o intervenție</p>
          
          <div className="contact-wrapper">
            <div className="contact-info reveal">
              <h3>Hai să vorbim!</h3>
              
              <div className="contact-item">
                <i className="fas fa-phone"></i>
                <div>
                  <a href="tel:+40771184705">0771 184 705</a>
                  <span>Lun-Vin: 08:00-18:00</span>
                </div>
              </div>
              
              <div className="contact-item">
                <i className="fas fa-envelope"></i>
                <div>
                  <a href="mailto:aerinstaliasi@gmail.com">aerinstaliasi@gmail.com</a>
                  <span>Răspundem în max. 24h</span>
                </div>
              </div>
              
              <div className="contact-item">
                <i className="fas fa-map-marker-alt"></i>
                <div>
                  <span>Iași, România</span>
                  <span>Acoperim tot județul</span>
                </div>
              </div>
              
              <div className="social-links">
                <a href="https://www.facebook.com/share/1Cd9UdTLyD/?mibextid=wwXIfr" target="_blank" rel="noopener noreferrer" aria-label="Facebook">
                  <i className="fab fa-facebook-f"></i>
                </a>
                <a href="https://wa.me/40771184705" target="_blank" rel="noopener noreferrer" aria-label="WhatsApp">
                  <i className="fab fa-whatsapp"></i>
                </a>
              </div>
              
              <div className="working-hours">
                <h4><i className="fas fa-clock"></i> Program de lucru</h4>
                <p>Luni – Vineri: 08:00 – 18:00</p>
                <p>Sâmbătă: 09:00 – 14:00</p>
                <p>Duminică: Închis</p>
              </div>
            </div>
            
            <div className="contact-form reveal">
              <div className="form-progress">
                {[1, 2, 3].map(step => (
                  <div 
                    key={step} 
                    className={`progress-step ${currentStep === step ? "active" : ""} ${currentStep > step ? "completed" : ""}`}
                  >
                    <div className="step-num">{step}</div>
                    <span>{step === 1 ? "Serviciu" : step === 2 ? "Date" : "Detalii"}</span>
                  </div>
                ))}
              </div>
              
              <form onSubmit={handleSubmit} data-testid="contact-form">
                {/* Step 1 */}
                <div className={`form-step ${currentStep === 1 ? "active" : ""}`}>
                  <h4 style={{ marginBottom: "1.5rem", fontFamily: "var(--font-heading)" }}>Alege Serviciul</h4>
                  <div className="service-selector">
                    {[
                      { id: "montaj", label: "Montaj AC", icon: "fa-snowflake", value: "Montaj Aer Condiționat" },
                      { id: "igienizare", label: "Igienizare", icon: "fa-spray-can", value: "Igienizare" },
                      { id: "freon", label: "Încărcare Freon", icon: "fa-thermometer-half", value: "Încărcare Freon" },
                      { id: "carotare", label: "Carotare", icon: "fa-cog", value: "Carotare" }
                    ].map(srv => (
                      <div className="service-option" key={srv.id}>
                        <input 
                          type="radio" 
                          name="service" 
                          id={`srv-${srv.id}`} 
                          value={srv.value}
                          checked={formData.service === srv.value}
                          onChange={handleFormChange}
                          data-testid={`service-${srv.id}`}
                        />
                        <label htmlFor={`srv-${srv.id}`}>
                          <i className={`fas ${srv.icon}`}></i>
                          <span>{srv.label}</span>
                        </label>
                      </div>
                    ))}
                  </div>
                  
                  <div className="form-nav">
                    <div></div>
                    <button type="button" className="btn btn-primary" onClick={() => nextStep(2)} data-testid="form-next-step-1">
                      Continuă <i className="fas fa-arrow-right"></i>
                    </button>
                  </div>
                </div>
                
                {/* Step 2 */}
                <div className={`form-step ${currentStep === 2 ? "active" : ""}`}>
                  <h4 style={{ marginBottom: "1.5rem", fontFamily: "var(--font-heading)" }}>Datele Tale</h4>
                  
                  <input 
                    type="text" 
                    name="name" 
                    className="form-input" 
                    placeholder="Nume complet *" 
                    value={formData.name}
                    onChange={handleFormChange}
                    data-testid="form-name"
                  />
                  <input 
                    type="tel" 
                    name="phone" 
                    className="form-input" 
                    placeholder="Telefon * (ex: 0771184705)" 
                    value={formData.phone}
                    onChange={handleFormChange}
                    data-testid="form-phone"
                  />
                  <input 
                    type="email" 
                    name="email" 
                    className="form-input" 
                    placeholder="Email (opțional)"
                    value={formData.email}
                    onChange={handleFormChange}
                    data-testid="form-email"
                  />
                  <input 
                    type="text" 
                    name="address" 
                    className="form-input" 
                    placeholder="Adresă intervenție *"
                    value={formData.address}
                    onChange={handleFormChange}
                    data-testid="form-address"
                  />
                  
                  <label style={{ display: "block", marginBottom: "0.75rem", fontSize: "0.9rem" }}>Urgență:</label>
                  <div className="urgency-selector">
                    {[
                      { id: "urgent", label: "🔴 Urgent", value: "Urgent" },
                      { id: "week", label: "🟡 În această săptămână", value: "În această săptămână" },
                      { id: "planned", label: "🟢 Planificat", value: "Planificat" }
                    ].map(urg => (
                      <div className="urgency-option" key={urg.id}>
                        <input 
                          type="radio" 
                          name="urgency" 
                          id={urg.id} 
                          value={urg.value}
                          checked={formData.urgency === urg.value}
                          onChange={handleFormChange}
                        />
                        <label htmlFor={urg.id}>{urg.label}</label>
                      </div>
                    ))}
                  </div>
                  
                  <div className="form-nav">
                    <button type="button" className="btn btn-back" onClick={() => setCurrentStep(1)}>
                      <i className="fas fa-arrow-left"></i> Înapoi
                    </button>
                    <button type="button" className="btn btn-primary" onClick={() => nextStep(3)} data-testid="form-next-step-2">
                      Continuă <i className="fas fa-arrow-right"></i>
                    </button>
                  </div>
                </div>
                
                {/* Step 3 */}
                <div className={`form-step ${currentStep === 3 ? "active" : ""}`}>
                  <h4 style={{ marginBottom: "1.5rem", fontFamily: "var(--font-heading)" }}>Detalii Suplimentare</h4>
                  
                  <textarea 
                    name="message" 
                    className="form-input" 
                    placeholder="Descrie situația (opțional) - brand aparat, problema, etc."
                    value={formData.message}
                    onChange={handleFormChange}
                    data-testid="form-message"
                  ></textarea>
                  
                  <div className="form-nav">
                    <button type="button" className="btn btn-back" onClick={() => setCurrentStep(2)}>
                      <i className="fas fa-arrow-left"></i> Înapoi
                    </button>
                    <button type="submit" className={`btn btn-submit ${formLoading ? "loading" : ""}`} data-testid="form-submit" disabled={formLoading}>
                      {formLoading ? (
                        <>
                          <span className="loading-text">Se trimite...</span>
                          <span className="spinner"></span>
                        </>
                      ) : (
                        <span className="btn-text">Trimite Cererea <i className="fas fa-paper-plane"></i></span>
                      )}
                    </button>
                  </div>
                  
                  <p className="privacy-note">
                    <i className="fas fa-lock"></i> Datele tale sunt în siguranță. Le folosim doar pentru a te contacta în legătură cu serviciul solicitat.
                  </p>
                </div>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="container">
          <div className="footer-grid">
            <div className="footer-brand">
              <a href="#acasa" className="logo" onClick={(e) => scrollToSection(e, "acasa")}>
                <img 
                  src="https://customer-assets.emergentagent.com/job_aer-instal-iasi/artifacts/zsz1a59d_logoaerinstaliasi.png" 
                  alt="AER INSTAL IAȘI Logo" 
                  className="logo-img"
                />
                <span>AER INSTAL IAȘI</span>
              </a>
              <p>Confort termic, garantat în Iași. Montaj profesional, service de calitate și prețuri corecte.</p>
            </div>
            
            <div className="footer-col">
              <h4>Servicii</h4>
              <ul>
                <li><a href="#servicii" onClick={(e) => scrollToSection(e, "servicii")}>Montaj AC</a></li>
                <li><a href="#servicii" onClick={(e) => scrollToSection(e, "servicii")}>Igienizare</a></li>
                <li><a href="#servicii" onClick={(e) => scrollToSection(e, "servicii")}>Încărcare Freon</a></li>
                <li><a href="#servicii" onClick={(e) => scrollToSection(e, "servicii")}>Carotare</a></li>
              </ul>
            </div>
            
            <div className="footer-col">
              <h4>Informații</h4>
              <ul>
                <li><a href="#despre" onClick={(e) => scrollToSection(e, "despre")}>Despre Noi</a></li>
                <li><a href="#calculator" onClick={(e) => scrollToSection(e, "calculator")}>Calculator BTU</a></li>
                <li><a href="#contact" onClick={(e) => scrollToSection(e, "contact")}>Contact</a></li>
              </ul>
            </div>
            
            <div className="footer-col">
              <h4>Contact</h4>
              <ul>
                <li><a href="tel:+40771184705">0771 184 705</a></li>
                <li><a href="mailto:aerinstaliasi@gmail.com">aerinstaliasi@gmail.com</a></li>
                <li><span>Iași, România</span></li>
              </ul>
            </div>
          </div>
          
          <div className="footer-bottom">
            <p>© 2025 AER INSTAL IAȘI. Toate drepturile rezervate.</p>
          </div>
        </div>
      </footer>

      {/* WhatsApp Float Button */}
      <a 
        href="https://wa.me/40771184705" 
        target="_blank" 
        rel="noopener noreferrer" 
        className="whatsapp-float" 
        aria-label="WhatsApp"
        data-testid="whatsapp-float"
      >
        <i className="fab fa-whatsapp"></i>
      </a>

      {/* Mobile Sticky Bar */}
      <div className="mobile-sticky">
        <div className="mobile-sticky-inner">
          <a href="tel:+40771184705" className="call-btn" data-testid="mobile-call-btn">
            <i className="fas fa-phone"></i> Sună Acum
          </a>
          <a href="https://wa.me/40771184705" className="wa-btn" target="_blank" rel="noopener noreferrer" data-testid="mobile-whatsapp-btn">
            <i className="fab fa-whatsapp"></i> WhatsApp
          </a>
        </div>
      </div>

      {/* Toast Notification */}
      <div className={`toast ${toastType} ${showToast ? "show" : ""}`} data-testid="toast-notification">
        <i className={`fas ${toastType === "success" ? "fa-check-circle" : "fa-exclamation-circle"}`}></i>
        <span>{toastMessage}</span>
      </div>
    </div>
  );
}

export default App;
